import {
  WHEEL_SPIN_STATUSES,
  WHEEL_TIERS,
  type WheelSpinStatus,
  type WheelSpinsFilters,
  type WheelSpinsSummary,
  type WheelTier,
} from '@/api/types';

// The filters live in the URL, so a filtered view can be reloaded or sent to a colleague.

export const NO_FILTERS: WheelSpinsFilters = {
  playerUuid: '',
  tier: '',
  statuses: [],
  wheelUid: '',
  segmentUid: '',
  currency: '',
  from: '',
  to: '',
};

// What support has to act on: a credit that failed, or a draw whose outcome was never settled.
export const NEEDS_ATTENTION: readonly WheelSpinStatus[] = ['failed', 'drawn'];

const PARAM: Record<Exclude<keyof WheelSpinsFilters, 'statuses'>, string> = {
  playerUuid: 'player',
  tier: 'tier',
  wheelUid: 'wheel',
  segmentUid: 'prize',
  currency: 'currency',
  from: 'from',
  to: 'to',
};
const STATUS_PARAM = 'status';
const DAY = /^\d{4}-\d{2}-\d{2}$/;

// A hand-edited or stale link must not send middleware a value it would refuse, so anything
// unknown is dropped rather than passed on.
export function filtersFromParams(params: URLSearchParams): WheelSpinsFilters {
  const text = (name: string) => params.get(name)?.trim() ?? '';
  const day = (name: string) => (DAY.test(text(name)) ? text(name) : '');
  const tier = text(PARAM.tier);
  const statuses = text(STATUS_PARAM)
    .split(',')
    .filter((status): status is WheelSpinStatus => WHEEL_SPIN_STATUSES.includes(status as WheelSpinStatus));
  return {
    playerUuid: text(PARAM.playerUuid),
    tier: WHEEL_TIERS.includes(tier as WheelTier) ? (tier as WheelTier) : '',
    statuses: [...new Set(statuses)],
    wheelUid: text(PARAM.wheelUid),
    segmentUid: text(PARAM.segmentUid),
    currency: text(PARAM.currency).toUpperCase(),
    from: day(PARAM.from),
    to: day(PARAM.to),
  };
}

export function paramsFromFilters(filters: WheelSpinsFilters): URLSearchParams {
  const params = new URLSearchParams();
  (Object.keys(PARAM) as (keyof typeof PARAM)[]).forEach((name) => {
    if (filters[name]) params.set(PARAM[name], filters[name]);
  });
  if (filters.statuses.length) params.set(STATUS_PARAM, filters.statuses.join(','));
  return params;
}

export function activeFilterCount(filters: WheelSpinsFilters): number {
  const { statuses, from, to, ...rest } = filters;
  return Object.values(rest).filter(Boolean).length + (statuses.length ? 1 : 0) + (from || to ? 1 : 0);
}

export const sameStatuses = (a: readonly WheelSpinStatus[], b: readonly WheelSpinStatus[]): boolean =>
  a.length === b.length && a.every((status) => b.includes(status));

const shiftDay = (ymd: string, days: number): string =>
  new Date(Date.parse(`${ymd}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

export interface DayPreset {
  label: string;
  range(today: string): { from: string; to: string };
}

// Days are UTC, like the ledger's `day`; `today` is the current UTC day.
export const DAY_PRESETS: readonly DayPreset[] = [
  { label: 'Today', range: (today) => ({ from: today, to: today }) },
  { label: 'Yesterday', range: (today) => ({ from: shiftDay(today, -1), to: shiftDay(today, -1) }) },
  { label: 'Last 7 days', range: (today) => ({ from: shiftDay(today, -6), to: today }) },
  { label: 'Last 30 days', range: (today) => ({ from: shiftDay(today, -29), to: today }) },
  { label: 'All time', range: () => ({ from: '', to: '' }) },
];

export interface Option {
  value: string;
  label: string;
}

export interface FilterOptions {
  wheels: Option[];
  prizes: Option[];
  currencies: Option[];
}

// The dropdowns offer what the ledger holds (the unfiltered summary), not what the current
// filters leave, so picking one value never hides the others. Prizes narrow to the chosen
// wheel; without one, a slice uid shared by several wheels is one option with every label.
export function filterOptions(all: WheelSpinsSummary | undefined, wheelUid: string): FilterOptions {
  const prizes = (all?.byPrize ?? []).filter((prize) => !wheelUid || prize.wheelUid === wheelUid);
  const labelsBySlice = new Map<string, Set<string>>();
  prizes.forEach(({ segmentUid, segmentLabel }) => {
    const labels = labelsBySlice.get(segmentUid) ?? new Set<string>();
    if (segmentLabel) labels.add(segmentLabel);
    labelsBySlice.set(segmentUid, labels);
  });

  const byLabel = (a: Option, b: Option) => a.label.localeCompare(b.label, undefined, { numeric: true });
  return {
    wheels: [...new Set((all?.byPrize ?? []).map((prize) => prize.wheelUid))]
      .map((wheel) => ({ value: wheel, label: wheel }))
      .sort(byLabel),
    prizes: [...labelsBySlice]
      .map(([slice, labels]) => ({
        value: slice,
        label: labels.size ? `${[...labels].join(' / ')} (${slice})` : slice,
      }))
      .sort(byLabel),
    currencies: (all?.byCurrency ?? [])
      .flatMap(({ currency }) => (currency ? [{ value: currency, label: currency }] : []))
      .sort(byLabel),
  };
}
